/**
 * ContactSummary — the "Yhteydenottosi" block that opens a member-initiated
 * conversation (055 US6, FR-003).
 *
 * ── When it renders, and why the rule is what it is ─────────────────────────
 *
 * The customer observed in the prototype that a conversation the FUND starts
 * has no contact summary — it begins directly with the fund's message. That is
 * structural in the prototype rather than a special case: its fund-initiated
 * fixtures simply carry no summary item, and it renders the block on that item's
 * presence.
 *
 * Oma+ reproduces it from the contract's own direction signal:
 *
 *   show = description has content  AND  messages[0]?.isReply !== true
 *
 * An EMPTY thread counts as member-initiated: the member created the
 * conversation and the summary is all there is to show, so suppressing it would
 * blank the surface.
 *
 * Keying off the first message's AUTHOR would be the obvious-looking mistake —
 * the integration stamps its own service identity on member-authored posts, so
 * the author field cannot identify direction (see `MessageThread`).
 *
 * ── Why the body is text and not a definition list ──────────────────────────
 *
 * The prototype renders a structured `<dl>` because its fixtures hold
 * label/value pairs. Oma+'s equivalent content is the case `description`, which
 * the compose path already flattens to faithful `Label: value` lines upstream
 * (`is-case-routing-adapter.ts` `buildCaseDescription`). We render that text
 * with its whitespace preserved.
 *
 * Deliberately NOT parsed back into a `<dl>`: the structure was flattened
 * upstream, and re-deriving it would break on any value containing `: ` or a
 * newline — reconstructing structure we were not given, to gain markup the
 * flattened form already reads as.
 */

import type { JSX } from 'react';

import type { LanguageCode } from '../i18n/LanguagePort.js';
import { useTranslate } from '../i18n/useTranslate.js';
import type { CaseMessageView } from './CasesReaderPort.js';
import { formatMessageDateTime } from './formatMessageDateTime.js';

/**
 * Whether the conversation should show a contact summary (FR-003).
 *
 * Exported so the decision is testable on its own and cannot drift from the
 * component that applies it.
 */
export function shouldShowContactSummary(
  description: string | null,
  messages: ReadonlyArray<CaseMessageView>,
): boolean {
  if (description === null || description.trim().length === 0) {
    return false;
  }
  // Fund-initiated → no summary. Empty thread → member-initiated.
  return messages[0]?.isReply !== true;
}

export function ContactSummary({
  description,
  createdAt,
  active,
}: {
  readonly description: string;
  readonly createdAt: string;
  readonly active: LanguageCode;
}): JSX.Element {
  const t = useTranslate();
  return (
    <div className="rounded-lg border border-border border-l-4 border-l-primary bg-muted/40 px-4 py-3">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t('myCases.detail.contactSummaryHeading')}
        </h3>
        <time className="whitespace-nowrap text-xs text-muted-foreground" dateTime={createdAt}>
          {formatMessageDateTime(createdAt, active)}
        </time>
      </div>
      <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground">
        {description}
      </p>
    </div>
  );
}
